import { ReactNode, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useTranslation } from '@/i18n';
import { Spinner } from '@/components/ui';
import { AccountNotConfigured } from './AccountNotConfigured';
import { shouldBlockAsUnconfigured } from './accessGate';
import { isIframeEnvironment, isAllowedOrigin } from '@/utils/ndEnvironment';
import { isTeamsTab, initTeams, teamsSignIn, findTeamsFirm } from '@/utils/teamsHost';
import {
  isExtensionHost,
  findExtensionFirm,
  fetchExtensionFirmBranding,
  reportPanelBranding,
  isExtensionSignedOut,
  clearExtensionSignedOut,
  panelHostKind,
  requestPanelSignIn,
  type ExtensionFirm,
  type PanelBranding,
} from '@/utils/extensionHost';
import { config } from '@/config/environment';
import { TeamsSignIn } from './TeamsSignIn';
import { ExtensionFirmMismatch } from './ExtensionFirmMismatch';

/** The tenant id in an access token ('' for the host), or null when it cannot be read. */
const tokenTenantId = (accessToken: string | undefined): string | null => {
  try {
    const part = (accessToken || '').split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(part)) as { tenantid?: string };
    return (claims.tenantid || '').toLowerCase();
  } catch {
    return null;
  }
};

interface AuthGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Protects routes that require authentication.
 * Redirects to login if user is not authenticated.
 *
 * In an iframe environment (NetDocuments), shows a login button that opens
 * a popup window instead of redirecting — since OIDC redirects don't work
 * inside cross-origin iframes.
 */
export const AuthGuard = ({ children, fallback }: AuthGuardProps) => {
  const { isAuthenticated, isLoading, isLoggingOut, login, permissions, permissionsResolved, user, currentTenantName } = useAuth();
  const { t } = useTranslation();
  const [isInIframe] = useState(() => isIframeEnvironment());
  const popupRef = useRef<Window | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval>>();
  const [inTeams] = useState(() => isInIframe && isTeamsTab());
  const [inExtension] = useState(() => isInIframe && isExtensionHost());
  // Chrome extension: the firm of the Gmail account next to the panel, so sign-in goes to it.
  const [extensionFirm, setExtensionFirm] = useState<ExtensionFirm | null>(null);
  // That firm's logo, name and colours, for the sign-in card and the panel's top bar.
  const [extensionBranding, setExtensionBranding] = useState<PanelBranding | null>(null);
  // Teams / Outlook / Microsoft 365 tab, signed out: the firm the sign-in goes to and its branding.
  const [teamsFirm, setTeamsFirm] = useState<ExtensionFirm | null>(null);
  const [teamsBranding, setTeamsBranding] = useState<PanelBranding | null>(null);
  // The user chose to stay in the firm they are signed in to (this page load only).
  const [keepSignedInFirm, setKeepSignedInFirm] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);

  // Tell Teams / Outlook the tab has loaded.
  useEffect(() => {
    if (inTeams) void initTeams();
  }, [inTeams]);

  // Clean up poll timer on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };
  }, []);

  // Listen for auth-complete from popup.
  // Popup sends actual OIDC user data via postMessage because the iframe's
  // localStorage is partitioned (third-party context).
  // Also listen via BroadcastChannel as a fallback — window.opener can
  // become null after cross-origin OIDC redirects (e.g., COOP headers).
  useEffect(() => {
    if (!isInIframe) return;

    const handleAuthComplete = (data: { oidcStorageKey?: string; oidcUserJson?: string }) => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }

      // Store the OIDC user data in the iframe's own localStorage
      const { oidcStorageKey, oidcUserJson } = data;
      if (oidcStorageKey && oidcUserJson) {
        localStorage.setItem(oidcStorageKey, oidcUserJson);
        clearExtensionSignedOut();
      }

      window.location.reload();
    };

    const handleMessage = (event: MessageEvent) => {
      if (!isAllowedOrigin(event.origin)) return;
      if (event.data?.type !== 'motr:auth-complete') return;
      handleAuthComplete(event.data);
    };

    window.addEventListener('message', handleMessage);

    // BroadcastChannel fallback for when window.opener is null
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel('motr:auth');
      bc.onmessage = (event) => {
        if (event.data?.type !== 'motr:auth-complete') return;
        handleAuthComplete(event.data);
      };
    } catch {
      // BroadcastChannel not supported — rely on postMessage only
    }

    return () => {
      window.removeEventListener('message', handleMessage);
      try { bc?.close(); } catch { /* ignore */ }
    };
  }, [isInIframe]);

  // Looked up before any click (window.open must run inside the click, not after an await), and
  // also when signed in: a stored session can belong to another firm than the Gmail account.
  useEffect(() => {
    if (!inExtension || isLoading) return;
    let cancelled = false;
    findExtensionFirm(config.apiUrl).then((firm) => { if (!cancelled) setExtensionFirm(firm); });
    return () => { cancelled = true; };
  }, [inExtension, isLoading]);

  // Teams tab, signed out: which firm the sign-in goes to (the address, or the Microsoft 365
  // user's email domain), branded like the extension's card.
  useEffect(() => {
    if (!inTeams || isLoading || isAuthenticated) return;
    let cancelled = false;
    findTeamsFirm(config.apiUrl).then(async (firm) => {
      if (cancelled || !firm) return;
      setTeamsFirm(firm);
      const branding = await fetchExtensionFirmBranding(config.apiUrl, firm);
      if (!cancelled && branding) setTeamsBranding(branding);
    });
    return () => { cancelled = true; };
  }, [inTeams, isLoading, isAuthenticated]);

  // Signed out: brand the sign-in card and the panel's bar as the firm. Once signed in, the app
  // shell reports the signed-in firm's branding instead (PanelBrandingReporter).
  useEffect(() => {
    if (!inExtension || isAuthenticated || !extensionFirm) return;
    let cancelled = false;
    fetchExtensionFirmBranding(config.apiUrl, extensionFirm).then((branding) => {
      if (cancelled || !branding) return;
      setExtensionBranding(branding);
      reportPanelBranding(branding);
    });
    return () => { cancelled = true; };
  }, [inExtension, isAuthenticated, extensionFirm]);

  // ── Popup-based login for iframe environments ──────────────────────────
  const openLoginPopup = useCallback((options?: { askForPassword?: boolean }) => {
    // Teams desktop blocks window.open; Teams opens the sign-in popup itself.
    if (inTeams) {
      setSignInError(null);
      setSigningIn(true);
      teamsSignIn(teamsFirm ? teamsFirm.tenantId : null)
        .then(({ oidcStorageKey, oidcUserJson }) => {
          if (oidcStorageKey && oidcUserJson) localStorage.setItem(oidcStorageKey, oidcUserJson);
          window.location.reload();
        })
        .catch((err: unknown) => {
          const reason = err instanceof Error ? err.message : String(err);
          setSignInError(/CancelledByUser/i.test(reason)
            ? t('Auth:SignInCancelled', { defaultValue: 'Sign-in was cancelled.' })
            : t('Auth:SignInFailed', { defaultValue: 'Sign-in failed: {{reason}}', reason }));
          setSigningIn(false);
        });
      return;
    }

    // After "Sign out" in the panel, ask for the password rather than reuse mot-r's login cookie.
    // Switching firm too: otherwise the popup's existing mot-r login (the other firm) is reused.
    const askForPassword = inExtension && (options?.askForPassword || isExtensionSignedOut());

    // Outlook desktop blocks popups: the task pane runs sign-in in an Office dialog instead.
    if (inExtension && panelHostKind() === 'outlook') {
      setSignInError(null);
      setSigningIn(true);
      requestPanelSignIn(extensionFirm ? extensionFirm.tenantId : null, askForPassword ? 'login' : undefined)
        .then(({ oidcStorageKey, oidcUserJson }) => {
          localStorage.setItem(oidcStorageKey, oidcUserJson);
          clearExtensionSignedOut();
          window.location.reload();
        })
        .catch((err: unknown) => {
          setSignInError(err instanceof Error ? err.message : String(err));
          setSigningIn(false);
        });
      return;
    }

    const width = 600;
    const height = 850;
    const left = window.screenX + (window.innerWidth - width) / 2;
    const top = window.screenY + (window.innerHeight - height) / 2;

    // The firm rides on the popup URL; AuthProvider passes it on as __tenant ('' = host).
    const tenantParam = extensionFirm ? `&__tenant=${encodeURIComponent(extensionFirm.tenantId)}` : '';
    const promptParam = askForPassword ? '&prompt=login' : '';
    popupRef.current = window.open(
      window.location.origin + '?popup=login' + tenantParam + promptParam,
      'loginWindow',
      `toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=${width},height=${height},left=${left},top=${top}`,
    );

    // Fallback: poll for popup close. Since popup & parent share localStorage
    // (same origin), oidc-client-ts state is already available — just reload.
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
    }
    pollTimerRef.current = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = undefined;
        window.location.reload();
      }
    }, 500);
  }, [inTeams, inExtension, extensionFirm, teamsFirm, t]);

  useEffect(() => {
    // Don't trigger login redirect while a logout is in progress.
    if (!isLoading && !isAuthenticated && !isLoggingOut) {
      if (!isInIframe) {
        // Normal flow: redirect to OIDC provider
        login();
      }
      // In iframe: don't auto-redirect, show login button instead
    }
  }, [isLoading, isAuthenticated, isLoggingOut, login, isInIframe]);

  if (isLoading) {
    return fallback || <Spinner mode="fullscreen" text={t('Loading...', { defaultValue: 'Loading...' })} />;
  }

  if (!isAuthenticated) {
    // Teams / Outlook / Microsoft 365 tab: mot-r-styled sign-in card.
    if (inTeams) {
      return fallback || <TeamsSignIn
        onSignIn={() => openLoginPopup()}
        error={signInError}
        busy={signingIn}
        firmName={teamsFirm?.name}
        branding={teamsBranding}
      />;
    }

    // Chrome extension side panel: same card; sign-in runs in the generic popup below.
    if (inExtension) {
      return fallback || <TeamsSignIn
        onSignIn={() => openLoginPopup()}
        error={signInError}
        busy={signingIn}
        host={panelHostKind() === 'outlook' ? 'outlook' : 'extension'}
        firmName={extensionFirm?.name}
        branding={extensionBranding}
      />;
    }

    // In iframe: show a login button (mirrors Angular home.component.html)
    if (isInIframe) {
      return (
        fallback || (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100vh',
              gap: '16px',
              fontFamily: 'system-ui, sans-serif',
            }}
          >
            <h3 style={{ margin: 0, color: '#333' }}>{t('Auth:PleaseLogin', { defaultValue: 'Please login' })}</h3>
            <button
              type="button"
              onClick={() => openLoginPopup()}
              style={{
                padding: '10px 24px',
                fontSize: '14px',
                fontWeight: 500,
                color: '#fff',
                backgroundColor: '#6c757d',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              {t('Login', { defaultValue: 'Login' })}
            </button>
          </div>
        )
      );
    }

    return fallback || <Spinner mode="fullscreen" text={isLoggingOut ? t('Auth:LoggingOut', { defaultValue: 'Logging out...' }) : t('Auth:RedirectingToLogin', { defaultValue: 'Redirecting to login...' })} />;
  }

  // Chrome extension: the stored session is for another firm than the Gmail account's.
  const signedInTenant = inExtension ? tokenTenantId(user?.access_token) : null;
  if (
    inExtension && extensionFirm && !keepSignedInFirm && signedInTenant !== null &&
    signedInTenant !== extensionFirm.tenantId.toLowerCase()
  ) {
    const profile = user?.profile;
    return (
      <ExtensionFirmMismatch
        firmName={extensionFirm.name || t('Auth:YourFirm', { defaultValue: 'your firm' })}
        signedInFirm={currentTenantName || (signedInTenant ? t('Auth:AnotherFirm', { defaultValue: 'another firm' }) : t('Auth:MotrHostAccount', { defaultValue: 'the mot-r host account' }))}
        signedInUser={profile?.preferred_username || profile?.email || profile?.name || t('Auth:ThisUser', { defaultValue: 'this user' })}
        onSwitch={() => openLoginPopup({ askForPassword: true })}
        onContinue={() => setKeepSignedInFirm(true)}
      />
    );
  }

  // Authenticated but holds nothing. See accessGate.ts for why this is gated on
  // permissionsResolved rather than featuresLoaded.
  if (shouldBlockAsUnconfigured({ permissionsResolved, permissions })) {
    return <AccountNotConfigured />;
  }

  return <>{children}</>;
};
